'use client';

/**
 * @fileoverview `useDiscoverPersonalization` — shared client hook that resolves
 * the gated personalized-chrome model (FRE-917) for the Discover surface.
 *
 * Reads the viewer from `useAuth()` and the taste data from the SHARED
 * `useDiscoverEventsQuery(buildDiscoverQueryArgs(...))` subscription — the SAME
 * cache entry the results bar / list view already subscribe to. Reusing that
 * subscription is load-bearing: it keeps the cold-load single-fetch invariant
 * intact (no second `/events/discover` request) because RTK Query dedupes
 * structurally-identical args. See `apps/web/lib/discover/queryArgs.ts`.
 *
 * Returns `DiscoverPersonalization | null`. `null` means "render generic (A)
 * chrome" — the consumers (coord strip, hero, results bar) fall back to today's
 * non-personalized state. Non-null means the viewer is authenticated WITH a real
 * taste signal and the chrome should render the personalized layer.
 *
 * @since FRE-917
 */
import * as React from 'react';

import type { CityCode } from '@loci/city-editions';
import { useDiscoverEventsQuery } from '@loci/api';

import { useAuth } from '@/contexts/AuthContext';
import { useDiscoverParams } from '@/hooks/useDiscoverParams';
import { useDiscoverEventArgs } from '@/lib/discover/useDiscoverEventArgs';
import {
  buildDiscoverPersonalization,
  type DiscoverPersonalization,
} from '@/lib/discover/personalizedChrome';

/**
 * Resolve the gated personalized-chrome model for the active city + filter set.
 * Returns `null` when the personalized layer must not render (anonymous, still
 * soaking, or no envelope) so every consumer degrades to generic chrome.
 */
export function useDiscoverPersonalization(cityCode: CityCode): DiscoverPersonalization | null {
  const { user, isAuthenticated } = useAuth();
  const { params } = useDiscoverParams();

  const queryArgs = useDiscoverEventArgs(params, cityCode);
  // Shared subscription — same cache key as the results bar / list view, so no
  // extra network request fires (cold-load single-fetch invariant preserved).
  //
  // `currentData`, not `data`: RTK Query keeps `data` pointing at the last
  // successful response across an argument change, so after a filter change it
  // still carries the PREVIOUS filter set's totals. Those totals feed the coord
  // strip's "N of M match your taste" segment, and `useDiscoverEnvelopeTotals`
  // reads the argument-matched `currentData` — so reading `data` here would
  // paint a stale taste segment beside a skeletoning results bar, manufacturing
  // exactly the disagreeing-count state WEB-786 removes. Both hooks settle
  // together on `currentData`.
  const { currentData } = useDiscoverEventsQuery(queryArgs);

  return React.useMemo(
    () =>
      buildDiscoverPersonalization({
        user,
        isAuthenticated,
        envelope: currentData?.authenticatedEnvelope,
        personalized: currentData?.meta?.personalized,
        total: currentData?.meta?.total,
        tasteMatchedTotal: currentData?.meta?.tasteMatchedTotal,
      }),
    [user, isAuthenticated, currentData],
  );
}
